/**
 * Google Analytics Utility Class
 * Comprehensive event tracking for user behavior analysis
 */
export class GAUtils {
    /**
     * Core method to report events to Google Analytics via GTM
     */
    static reportEvent(eventName: string, params: Record<string, any> = {}) {
        // @ts-ignore
        if (typeof window.gtag === 'function') {
            // @ts-ignore
            window.gtag('event', eventName, params);
            console.log(`[GA] Event: ${eventName}`, params);
        } else {
            console.warn("[GA] gtag function not available.");
        }
    }

    // Legacy method for backward compatibility
    static async reportSearchToGTM(eventName: string, object: object) {
        this.reportEvent(eventName, object);
    }

    // ==========================================
    // AUTHENTICATION EVENTS
    // ==========================================

    /**
     * Track when user attempts to login
     */
    static trackLoginAttempt(method: 'google' | 'apple' | 'email' | 'telegram') {
        this.reportEvent('login_attempt', {
            method,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track successful login
     */
    static trackLoginSuccess(method: 'google' | 'apple' | 'email' | 'telegram', userId?: string) {
        this.reportEvent('login', {
            method,
            user_id: userId,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track login error
     */
    static trackLoginError(method: 'google' | 'apple' | 'email' | 'telegram', errorMessage: string, errorCode?: string) {
        this.reportEvent('login_error', {
            method,
            error_message: errorMessage,
            error_code: errorCode,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track user logout
     */
    static trackLogout(userId?: string) {
        this.reportEvent('logout', {
            user_id: userId,
            timestamp: new Date().toISOString(),
        });
    }

    // ==========================================
    // NAVIGATION EVENTS
    // ==========================================

    /**
     * Track page views
     */
    static trackPageView(pageName: string, pageTitle?: string) {
        this.reportEvent('page_view', {
            page_name: pageName,
            page_title: pageTitle || document.title,
            page_location: window.location.href,
            page_path: window.location.pathname,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track logo click
     */
    static trackLogoClick() {
        this.reportEvent('logo_click', {
            location: 'navbar',
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track navbar navigation clicks
     */
    static trackNavClick(destination: string) {
        this.reportEvent('nav_click', {
            destination,
            location: 'navbar',
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track footer link clicks
     */
    static trackFooterClick(linkName: string, linkUrl: string) {
        this.reportEvent('footer_click', {
            link_name: linkName,
            link_url: linkUrl,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track user dropdown menu interactions
     */
    static trackDropdownOpen() {
        this.reportEvent('dropdown_open', {
            location: 'navbar',
            timestamp: new Date().toISOString(),
        });
    }

    // ==========================================
    // SEARCH EVENTS
    // ==========================================

    /**
     * Track search query
     */
    static trackSearch(query: string, creditsRemaining?: number) {
        this.reportEvent('search', {
            search_term: query,
            credits_remaining: creditsRemaining,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track search keyword specifically for keyword analysis
     * This sends a dedicated event for analyzing popular search terms
     */
    static trackSearchKeyword(keyword: string, resultCount?: number, isCached?: boolean) {
        // Normalize keyword for consistent tracking
        const normalizedKeyword = keyword.trim().toLowerCase();
        const originalKeyword = keyword.trim();
        
        this.reportEvent('search_keyword', {
            keyword: originalKeyword,
            keyword_normalized: normalizedKeyword,
            keyword_length: originalKeyword.length,
            word_count: originalKeyword.split(/\s+/).length,
            results_count: resultCount,
            is_cached: isCached,
            timestamp: new Date().toISOString(),
        });

        // Also send to GA4's built-in search event for better integration
        this.reportEvent('view_search_results', {
            search_term: originalKeyword,
        });
    }

    /**
     * Track when search results are displayed
     */
    static trackSearchResultsView(query: string, resultCount?: number) {
        this.reportEvent('search_results_view', {
            search_term: query,
            results_count: resultCount,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track click on a search result
     */
    static trackSearchResultClick(query: string, resultUrl: string, resultPosition?: number) {
        this.reportEvent('search_result_click', {
            search_term: query,
            result_url: resultUrl,
            result_position: resultPosition,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track search pagination
     */
    static trackSearchPagination(query: string, pageNumber: number) {
        this.reportEvent('search_pagination', {
            search_term: query,
            page_number: pageNumber,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track search error
     */
    static trackSearchError(query: string, errorMessage: string) {
        this.reportEvent('search_error', {
            search_term: query,
            error_message: errorMessage,
            timestamp: new Date().toISOString(),
        });
    }

    // ==========================================
    // CREDIT & PURCHASE EVENTS
    // ==========================================

    /**
     * Track credit consumption
     */
    static trackCreditConsumed(query: string, creditsRemaining: number, isCached: boolean) {
        this.reportEvent('credit_consumed', {
            search_term: query,
            credits_remaining: creditsRemaining,
            is_cached: isCached,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track when user runs out of credits
     */
    static trackOutOfCredits(source: string) {
        this.reportEvent('out_of_credits', {
            source,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track balance page view
     */
    static trackBalancePageView(currentCredits: number) {
        this.reportEvent('balance_page_view', {
            current_credits: currentCredits,
            timestamp: new Date().toISOString(),
        });
    }

    // ==========================================
    // GA4 STANDARD ECOMMERCE EVENTS
    // ==========================================

    /**
     * Build a standard GA4 item for credit purchases
     */
    private static buildCreditItem(amount: number, credits: number) {
        return {
            item_id: 'credits',
            item_name: 'Search Credits',
            item_category: 'Digital Goods',
            price: amount,
            quantity: credits,
        };
    }

    /**
     * GA4 standard: view_item — user views the pricing/balance page
     */
    static trackViewItem(amount: number, credits: number) {
        this.reportEvent('view_item', {
            currency: 'USD',
            value: amount,
            items: [this.buildCreditItem(amount, credits)],
        });
    }

    /**
     * GA4 standard: add_to_cart — user selects a purchase amount
     */
    static trackAddToCart(amount: number, credits: number) {
        this.reportEvent('add_to_cart', {
            currency: 'USD',
            value: amount,
            items: [this.buildCreditItem(amount, credits)],
        });
    }

    /**
     * GA4 standard: begin_checkout — user clicks the purchase button
     */
    static trackBeginCheckout(amount: number, credits: number) {
        this.reportEvent('begin_checkout', {
            currency: 'USD',
            value: amount,
            items: [this.buildCreditItem(amount, credits)],
        });
    }

    /**
     * Track purchase amount selection
     */
    static trackPurchaseAmountSelect(amount: number, credits: number, isCustom: boolean) {
        this.reportEvent('purchase_amount_select', {
            amount_usd: amount,
            credits_selected: credits,
            is_custom_amount: isCustom,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track purchase attempt
     */
    static trackPurchaseAttempt(amount: number, credits: number) {
        this.reportEvent('purchase_attempt', {
            amount_usd: amount,
            credits_selected: credits,
            currency: 'USD',
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track successful purchase
     */
    static trackPurchaseSuccess(amount: number, credits: number, transactionId?: string) {
        this.reportEvent('purchase', {
            transaction_id: transactionId || `txn_${Date.now()}`,
            value: amount,
            currency: 'USD',
            items: [this.buildCreditItem(amount, credits)],
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track canceled purchase
     */
    static trackPurchaseCanceled(amount?: number, credits?: number) {
        this.reportEvent('purchase_canceled', {
            amount_usd: amount,
            credits_selected: credits,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track purchase error
     */
    static trackPurchaseError(amount: number, errorMessage: string) {
        this.reportEvent('purchase_error', {
            amount_usd: amount,
            error_message: errorMessage,
            timestamp: new Date().toISOString(),
        });
    }

    // ==========================================
    // SUBSCRIPTION EVENTS
    // ==========================================

    /**
     * Build a standard GA4 item for Pro subscription
     */
    private static buildSubscriptionItem(price: number) {
        return {
            item_id: 'pro_monthly',
            item_name: 'Pro Subscription',
            item_category: 'Subscription',
            price,
            quantity: 1,
        };
    }

    /**
     * Track tab switch on balance page (pro vs credits)
     */
    static trackBalanceTabSwitch(tab: 'pro' | 'credits') {
        this.reportEvent('balance_tab_switch', {
            tab,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * GA4 standard: view_item — user views the Pro plan
     */
    static trackSubscriptionViewItem(price: number) {
        this.reportEvent('view_item', {
            currency: 'USD',
            value: price,
            items: [this.buildSubscriptionItem(price)],
        });
    }

    /**
     * Track subscribe button click
     */
    static trackSubscribeClick(price: number) {
        this.reportEvent('subscribe_click', {
            plan: 'pro_monthly',
            price,
            currency: 'USD',
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * GA4 standard: begin_checkout — user initiates subscription checkout
     */
    static trackSubscribeBeginCheckout(price: number) {
        this.reportEvent('begin_checkout', {
            currency: 'USD',
            value: price,
            items: [this.buildSubscriptionItem(price)],
        });
    }

    /**
     * GA4 standard: purchase — subscription purchase success
     */
    static trackSubscribeSuccess(price: number) {
        this.reportEvent('purchase', {
            transaction_id: `sub_${Date.now()}`,
            value: price,
            currency: 'USD',
            items: [this.buildSubscriptionItem(price)],
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track subscription checkout canceled
     */
    static trackSubscribeCanceled() {
        this.reportEvent('subscribe_canceled', {
            plan: 'pro_monthly',
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track subscription checkout or API error
     */
    static trackSubscribeError(errorMessage: string) {
        this.reportEvent('subscribe_error', {
            plan: 'pro_monthly',
            error_message: errorMessage,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track cancel subscription button click
     */
    static trackSubscriptionCancelClick() {
        this.reportEvent('subscription_cancel_click', {
            plan: 'pro_monthly',
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track subscription cancel success
     */
    static trackSubscriptionCancelSuccess() {
        this.reportEvent('subscription_cancel_success', {
            plan: 'pro_monthly',
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track subscription cancel error
     */
    static trackSubscriptionCancelError(errorMessage: string) {
        this.reportEvent('subscription_cancel_error', {
            plan: 'pro_monthly',
            error_message: errorMessage,
            timestamp: new Date().toISOString(),
        });
    }

    // ==========================================
    // MEMBERSHIP & PROMPT EVENTS
    // ==========================================

    /**
     * Track membership prompt view
     */
    static trackMembershipPromptView(source: string) {
        this.reportEvent('membership_prompt_view', {
            source,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track membership prompt click
     */
    static trackMembershipPromptClick(action: 'buy_credits' | 'close' | 'watch_ad' | 'go_pro') {
        this.reportEvent('membership_prompt_click', {
            action,
            timestamp: new Date().toISOString(),
        });
    }

    // ==========================================
    // AD EVENTS
    // ==========================================

    /**
     * Track rewarded ad click
     */
    static trackRewardedAdClick() {
        this.reportEvent('rewarded_ad_click', {
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track rewarded ad completed
     */
    static trackRewardedAdCompleted(rewardType: string) {
        this.reportEvent('rewarded_ad_completed', {
            reward_type: rewardType,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track rewarded ad error
     */
    static trackRewardedAdError(errorMessage: string) {
        this.reportEvent('rewarded_ad_error', {
            error_message: errorMessage,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track ad reward credit granted
     */
    static trackAdRewardCredited(newBalance: number) {
        this.reportEvent('ad_reward_credited', {
            new_balance: newBalance,
            timestamp: new Date().toISOString(),
        });
    }

    // ==========================================
    // USER ENGAGEMENT EVENTS
    // ==========================================

    /**
     * Track external link clicks
     */
    static trackExternalLinkClick(linkUrl: string, linkText: string) {
        this.reportEvent('external_link_click', {
            link_url: linkUrl,
            link_text: linkText,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track language change
     */
    static trackLanguageChange(language: string) {
        this.reportEvent('language_change', {
            language,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track button click (generic)
     */
    static trackButtonClick(buttonName: string, context: string) {
        this.reportEvent('button_click', {
            button_name: buttonName,
            context,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track error display to user
     */
    static trackErrorDisplay(errorType: string, errorMessage: string, context: string) {
        this.reportEvent('error_display', {
            error_type: errorType,
            error_message: errorMessage,
            context,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track session start
     */
    static trackSessionStart(userId?: string) {
        this.reportEvent('session_start', {
            user_id: userId,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track time spent on page
     */
    static trackTimeOnPage(pageName: string, durationSeconds: number) {
        this.reportEvent('time_on_page', {
            page_name: pageName,
            duration_seconds: durationSeconds,
            timestamp: new Date().toISOString(),
        });
    }
}
